/**
 * column-selection-input.component.ts
 * Copyright (c) 2017, My Coverage Plan, Inc.
 * All rights reserved.
 */

import { Component, EventEmitter, Input, Output } from "@angular/core";
import { AbstractControl, FormArray, FormControl, Validators }
	from "@angular/forms";
import { Modal } from "angular2-modal/plugins/bootstrap";
import { Subscription } from "rxjs/Rx";

import { NumberTypes } from "../common/data/data-types";
import { ColumnDescription }
	from "../server/messages/export/start-data-export-request";
import { MembershipInputComponent } from
	"../support/controls/membership-input.component";
import { Displayable } from "../support/types/aliases";
import { ModalHelpers } from "../support/utilities";
import { arraySwap, dot, oneline } from "../support/utilities/functions";

import { DefaultSummarization, NonDefaultSummarizations, SummarizationType }
	from "./summarization";
import { StdVariable } from "./typed-parameter";
import { FilterNode } from "../search/search-node";

import { AdvancedColumnSelectionModalComponent, CustomColumnProps } from "./advanced-column-selection-modal.component";

interface ColumnAnnotations
{
	iterator? : string | symbol;
	table? : string;
	summarization? : SummarizationType;
	membershipClass? : string;
	debugCollectionFilter? : string;
}
type ColumnVar = Displayable & StdVariable & ColumnAnnotations;


/**
 * The {@link ColumnSelectionInputComponent} is a special-purpose subclass of
 * {@link MembershipInputComponent} for constructing "column descriptions" when
 * building a {@link DataExport}. It has special features for selecting
 * {@link SummarizationType}s, entering custom names, and displaying all the
 * currently-selected iterators.
 */
@Component({
	selector: "column-selection-input",
	template: `
<div class="row">
	<div class="col-sm-5">
		<label>
			Available columns
			<span class="badge">{{options.length}}</span>
		</label>
		<select
			[size]="size()"
			class="form-control"
			[formControl]="selectedOption"
			(keyup.enter)="add()">
			<option *ngIf="!options?.length" disabled>
				(empty)
			</option>
			<option
				*ngFor="let item of options"
				[ngValue]="item"
				[class]="item.membershipClass || ''"
				[disabled]="item.membershipDisabled || false"
				(dblclick)="add()">
				{{item.displayName}}
			</option>
		</select>
	</div>
	<div class="col-sm-2 button-col">
		<div class="form-group">
			<label>Summarization</label>
			<select
				#available
				class="form-control darker-disables"
				[size]="summarizations.length + 1"
				[formControl]="summarization"
				(keyup.enter)="add()">
				<option
					[disabled]="!defaultEnabled()"
					(dblclick)="add()"
					[ngValue]="summarizationDefault">
					{{summarizationDefault.displayName}}
				</option>
				<option
					*ngFor="let s of summarizations"
					[disabled]="!summarizationEnabled(s.numericalOnly)"
					(dblclick)="add()"
					[ngValue]="s">
					{{s.displayName}}
				</option>
			</select>
		</div>
		<table class="left-brace">
			<tr>
				<td rowspan="3" class="brace">
					&#123;
				</td>
				<td>
					<button
						class="btn btn-xs btn-default"
						(click)="add()"
						[disabled]="disabled">
						Add Column
					</button>
				</td>
			</tr>
			<tr><td>
				<button
					class="btn btn-xs btn-default"
					(click)="addAdvanced()"
					[disabled]="disabled"
					title="Add with a custom name and/or a column filter...">
					Add Advanced...
				</button>
			</td></tr>
			<tr><td>
				<button
					class="btn btn-xs btn-warning"
					(click)="addAll()"
					[disabled]="disabled">
					Add All
				</button>
			</td></tr>
		</table>
		<table class="right-brace">
			<tr>
				<td>
					<button
						class="btn btn-xs btn-default"
						(click)="remove()"
						[disabled]="disabled">
						Remove Column
					</button>
				</td>
				<td rowspan="5" class="brace">
					&#125;
				</td>
			</tr>
			<tr><td>
				<button
					class="btn btn-xs btn-default"
					(click)="edit()"
					[disabled]="disabled">
					Edit...
				</button>
			</td></tr>
			<tr><td>
				<button
					class="btn btn-xs btn-default"
					(click)="move(-1)"
					[disabled]="disabled">
					Move Up
				</button>
			</td></tr>
			<tr><td>
				<button
					class="btn btn-xs btn-default"
					(click)="move(1)"
					[disabled]="disabled">
					Move Down
				</button>
			</td></tr>
			<tr><td>
				<button
					class="btn btn-xs btn-danger"
					(click)="reset()"
					[disabled]="disabled">
					Reset
				</button>
			</td></tr>
		</table>
		<div class="iterator-list">
			<label>
				Repetitions
				<span class="badge">{{uniqueIterators.size}}</span>
			</label>
			<ul>
				<li *ngFor="let it of uniqueIterators">
					{{it}}
				</li>
			</ul>
		</div>
	</div>
	<div class="col-sm-5">
		<label>
			Selected columns
			<span class="badge">{{target.length}}</span>
		</label>
		<select
			#selected
			[size]="size()"
			class="form-control"
			[formControl]="selectedTarget"
			(keyup.enter)="add()"
			(keyup.delete)="remove()"
			(keyup.backspace)="remove()"
			draggable="true"
			(dragstart)="startDrag($event)">
			<option *ngIf="!target?.length" disabled>
				(empty)
			</option>
			<option
				*ngFor="let item of target"
				[ngValue]="item"
				[class]="item.membershipClass"
				[disabled]="item.membershipDisabled"
				(dblclick)="edit()"
				(click)="$event.preventDefault()"
				draggable="true"
				(dragstart)="startDrag($event)"
				(dragover)="dragOver($event)"
				(drop)="drop($event)">
				{{item.displayName}}{{item.filter ? " [filtered]" : ""}}
			</option>
		</select>
	</div>
</div>
	`,
	styles: [`
		.button-col {
			padding-left: 0;
			padding-right: 0;
		}
		.button-col .form-group:first-of-type {
			margin-top: 0;
		}
		.button-col button, .button-col table {
			width: 100%;
		}
		select.darker-disables option[disabled] {
			background: #eee;
		}
		option.special1 {
			font-style: italic;
			color: var(--color-blue);
		}
		option.special2 {
			font-style: italic;
			color: var(--color-green);
		}
		.iterator-list {
			margin-top: 1.5em;
		}
		.iterator-list ul {
			padding-left: 10px;
		}
		td.brace {
			font-size: 4.5em;
			line-height: 1.0;
		}
		.left-brace {
			margin-left: -6px;
		}
		.right-brace {
			margin-left: 6px;
		}
		.left-brace + .right-brace {
			margin-top: 10px;
		}
		.left-brace:hover .brace, .right-brace:hover .brace {
			color: #777;
		}
	`]
})
export class ColumnSelectionInputComponent extends MembershipInputComponent
{
	/**
	 * Local access to the global list of summarizations (Angular templates can
	 * only reference class properties.)
	 */
	public readonly summarizations : SummarizationType[] =
		NonDefaultSummarizations;

	/**
	 * The limit on how many different "iterators" (tables or non-table lists)
	 * can be added to the result. By practice, it's usually 1 or unlimited.
	 */
	@Input()
	public maxIterators : number = Number.MAX_SAFE_INTEGER;

	private _target : ColumnVar[] = [];
	@Input()
	public set target (columns : ColumnVar[])
	{
		if (columns)
		{
			if (this._target && this._target.length > 0)
			{
				this.reset();
			}
			columns.forEach((v : ColumnVar) => {
				if (v.cardinality !== 1)
				{
					if (v.summarization !== this.summarizationDefault)
					{
						this.addIterator(v);
						v.membershipClass = "text-success";
					}
					else
					{
						v.membershipClass = "text-info";
					}
				}
			});
		}
		this._target = columns;
	}
	public get target () : ColumnVar[]
	{
		return this._target;
	}

	/**
	 * A map of the names of the currently selected iterators. By convention the
	 * names of lone lists are wrapped in «guillemets».
	 */
	public uniqueIterators : Set<string | symbol> = new Set();

	/**
	 * The {@link FormControl} for the summarization method of the currently
	 * selected variable.
	 */
	public summarization : FormControl =
		new FormControl(null, Validators.required);

	/**
	 * The name of the default, non-summarizing option for summarization. Note
	 * that this name is not part of the {@link SummarizationTypes} array.
	 */
	public readonly summarizationDefault : SummarizationType =
		DefaultSummarization;

	public constructor (public modal : Modal)
	{
		super();
		// When the selection changes, reset the summarization.
		this.selectedOption.valueChanges.subscribe((val : any) => {
			if (val === null)
			{
				// If nothing is selected, clear the value and disable the
				// summarization field entirely. (This makes it more obvious you
				// can't select a summarization type and *then* a column.)
				return this.summarization.reset({value: null, disabled: true});
			}
			else
			{
				if (this.defaultEnabled())
				{
					// If the default choice is available, select it.
					this.summarization.reset(
						{value: this.summarizationDefault, disabled: false});
				}
				else
				{
					// If the default is unavailable, *don't select anything*,
					// as the other options should only be selected deliberately
					// to ensure it is an end-user choice.
					this.summarization.reset({value: null, disabled: false});
				}
			}
		});
		// When summarization changes, change the generated title. (This will
		// also trigger when the selection changes because that always forces a
		// summarization reset.)
		this.summarization.valueChanges.subscribe(() => this.resetTitle());
	}

	/**
	 * The name of the column being added.
	 */
	protected columnTitle : FormControl =
		new FormControl(null, Validators.required);

	/**
	 * Generate a new default title for the current selected field and chosen
	 * summarization method.
	 */
	protected resetTitle () : void
	{
		const field : string = this.selectedOption.value;
		if (field === null)
		{
			this.columnTitle.reset("");
		}
		else
		{
			const summarization : string = this.summarization.value;
			if (summarization)
			{
				this.columnTitle.reset(`${summarization} of ${field}`);
			}
			else
			{
				this.columnTitle.reset(`${field}`);
			}
		}
	}

	/**
	 * We use the control array to trigger disabling of the form and to hold
	 * collective status flags (valid/modified/etc) without requiring the parent
	 * to understand this class' internal control structure.
	 *
	 * The set implementation detaches the controls from the previous host and
	 * then pushes them into the new host.
	 */
	@Input()
	public set controlArray (c : AbstractControl)
	{
		if (c instanceof AbstractControl)
		{
			if (this.sub)
			{
				this.sub.unsubscribe();
			}
			this.syncEnabledness(c);
			this.sub = c.statusChanges.subscribe(
				(v : any) => this.syncEnabledness(c));
		}
	}
	protected syncEnabledness (c : AbstractControl) : void
	{
		this.disabled = c.disabled;
		if (c.disabled)
		{
			this.summarization.disable();
			this.selectedOption.disable();
			this.selectedTarget.disable();
		}
		else
		{
			this.summarization.enable();
			this.selectedOption.enable();
			this.selectedTarget.enable();
		}
	}
	protected sub : Subscription;

	public disabled : boolean = false;

	/**
	 * Whether the currently selected field supports summarizations. Effectively
	 * whether it's a scalar or not.
	 * @param numericalOnly Whether to also filter out non-number typed fields.
	 */
	public summarizationEnabled (numericalOnly : boolean) : boolean
	{
		if (this.selectedOption.value)
		{
			if ((this.selectedOption.value as StdVariable).cardinality === 1)
			{
				return false;
			}
			else
			{
				return !numericalOnly || NumberTypes.has(
					(this.selectedOption.value as StdVariable).type);
			}
		}
		else
		{
			// if nothing is selected, we leave all the summarizations enabled
			return true;
		}
	}

	/**
	 * Whether the currently selected field supports the default (non-)summary.
	 * This is also often just a scalar vs vector check, but notably it must
	 * also examine whether we've hit out "iterator limit".
	 */
	public defaultEnabled (selection? : ColumnVar) : boolean
	{
		selection = selection || this.selectedOption.value;
		if (selection)
		{
			if (selection.cardinality === 1)
			{
				return true;
			}
			else
			{
				return (
					(this.uniqueIterators.has(
						this.iteratorFor(selection)))
					|| (this.uniqueIterators.size < this.maxIterators));
			}
		}
		else
		{
			// if nothing is selected, we leave the default enabled
			return true;
		}
	}

	/**
	 * Find the canonical name of the iterator for a list, which will either be
	 * the name of its table, or a modified version of its own name.
	 * @param item The variable to generate an iterator name for
	 */
	protected iteratorFor (item : StdVariable & {table? : string})
		: string | symbol
	{
		if (item.table)
		{
			return item.table;
		}
		else
		{
			// Note: I used to use symbols (to prevent table name-variable name
			// conflicts), but they're annoying to convert back to strings in
			// the UI.
			return `«${item.name}»`;
		}
	}

	/**
	 * The minimum size of the source and target boxes. See the parent method
	 * {@link MembershipInputComponent#size}.
	 */
	protected readonly min : number = 10;

	/**
	 * Clear the selections and reset the available choices to the original
	 * array of options.
	 */
	public reset () : any[]
	{
		this.uniqueIterators.clear();
		return super.reset();
	}

	protected verifyAdd () : ColumnVar
	{
		const item : ColumnVar = this.selectedOption.value;
		if (item === null)
		{
			return;
		}
		const i : number = this.options.indexOf(item);
		if (i === -1)
		{
			throw new Error(`Could not find item in options: ${item}`);
		}
		if (this.summarization.invalid)
		{
			alert(oneline`
				You must select a summarization method for this field.
				(This may be due to selecting a list or table field and being
				beyond your iterator limit.)`);
			return;
		}
		return item;
	}

	/**
	 * Remove a value from options and add it to target.
	 */
	public add (custom? : CustomColumnProps) : void
	{
		const item : ColumnVar = this.verifyAdd();
		if (!item)
		{
			return;
		}

		const summarization : SummarizationType = this.summarization.value;
		const addlProps : any = Object.assign({}, custom);

		const matching : ColumnVar = this.target.find(
			(match : ColumnVar) => (
				match.name === item.name
				&& match.summarization === summarization));
		if (matching)
		{
			return alert(oneline`
				You already have <em>${item.name}</em> as a column
				with summarization <code>${summarization.displayName}</code>.`);
		}
		if (summarization === this.summarizationDefault)
		{
			if (item.cardinality !== 1)
			{
				this.addIterator(item);
			}
		}
		else
		{
			addlProps.displayName = addlProps.displayName
				|| `${summarization.displayName} of ${item.displayName}`;
			addlProps.membershipClass = "text-success";
				}
				
				// TODO
				// if (debugCollectionFilter)
				// {
				//	addlProps.debugCollectionFilter = debugCollectionFilter;
				// }

		this.target.push(Object.assign(
			{},
			item,
			{summarization},
			addlProps
		));

		this.selectedOption.reset();
		this.change.emit(this.target);
	}

	/**
	 * Add a column with advanced settings, such as a custom label or a 
	 * collection filter.
	 */
	public addAdvanced () : void
	{
		let variable = this.verifyAdd()
		if (!variable)
		{
			return;
		}
		ModalHelpers.observeCustom(
			this.modal,
			AdvancedColumnSelectionModalComponent,
			{size: "lg", variable, filterVariables: this._source}
		).subscribe(
			(custom : CustomColumnProps) => custom ? this.add(custom) : null);
	}

	public edit () : void
	{
		const item : any = this.selectedTarget.value;
		if (item === null)
		{
			return;
		}

		ModalHelpers.observeCustom(
			this.modal,
			AdvancedColumnSelectionModalComponent,
			{size: "lg", variable: item, filterVariables: this._source}
		).subscribe(
			(custom : CustomColumnProps) => {
				if (custom)
				{
					const i : number = this.target.indexOf(item);
					this.target.splice(i, 1, Object.assign({}, item, custom));
				}
			});
	}

	/**
	 * Add an item to the set of iterator names, and remember that name so we
	 * don't have to keep regenerating it.
	 * @param item The field being added to the column list
	 */
	protected addIterator (item : StdVariable & {iterator? : string | symbol})
		: void
	{
		item.iterator = this.iteratorFor(item);
		this.uniqueIterators.add(item.iterator);
	}

	/**
	 * Check if an item is the last instance of a field with a particular
	 * iterator, and if so, remove that iterator name from the set of those
	 * currently being used.
	 * @param item The field being removed
	 */
	protected removeIterator (item : ColumnVar)
		: void
	{
		if (!item.iterator)
		{
			return;
		}
		if (!this.target.some(
			(cmp : ColumnVar) =>
				cmp !== item && cmp.iterator === item.iterator))
		{
			this.uniqueIterators.delete(item.iterator);
		}
	}

	/**
	 * Remove a value from target and return it to options.
	 */
	public remove () : void
	{
		const item : any = this.selectedTarget.value;
		if (item === null)
		{
			return;
		}
		const i : number = this.target.indexOf(item);
		if (i === -1)
		{
			throw new Error(`Could not find item in options: ${item}`);
		}

		this.removeIterator(item);
		this.target.splice(i, 1);
		this.selectedTarget.reset();
		this.change.emit(this.target);
	}

	/**
	 * Add all of the variables in the options array to the target array.
	 * Since it may not be possible to add all of the non-scalar values without
	 * summarization strategies, this adds un-summarized values in order until
	 * it hits the maxIterator count and then skips all remaining.
	 *
	 * It's expected that this is mostly used for debugging or other large data
	 * operations.
	 */
	public addAll () : void
	{
		this.options.forEach((opt : Displayable & StdVariable) => {
			if (this.defaultEnabled(opt)
				&& this.target.find(
					(match : ColumnVar) =>
						(match.name === opt.name
							&& match.summarization === DefaultSummarization)
				) === undefined)
			{
				this.target.push(
					Object.assign(
						{},
						opt,
						{summarization: DefaultSummarization}));
				if (opt.cardinality !== 1)
				{
					this.addIterator(opt);
				}
			}
		});

		this.selectedOption.reset();
		this.change.emit(this.target);
	}

	public move (delta : number) : void
	{
		const item : ColumnVar = this.selectedTarget.value;
		if (!item)
		{
			return;
		}
		const index : number = this.target.indexOf(item);
		const finalPos : number = index + delta;
		try
		{
			arraySwap(this.target, index, index + delta);
			this.change.emit(this.target);
		}
		catch (err)
		{
			return;
		}
	}

	public startDrag (e : DragEvent)
	{
		console.log("Drag started:", e);
	}
	public dragOver (e : DragEvent)
	{
		console.log("Drag started:", e);
	}
	public drop (e : DragEvent)
	{
		console.log("Drag started:", e);
	}
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/aggregation/column-selection-input.component.ts